/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Collapse,
  Flex,
  Form,
  Input,
  Row,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import { ClearOutlined, SearchOutlined } from "@ant-design/icons";
import ReportService from "../../../../service/ERP/Report.service";
import {
  saveAccessPageStateCookie,
  loadAccessPageStateCookie,
  clearAccessPageStateCookie,
} from "../../../../utils/accessPageStateCookie";
import { minStockAlertColumns, netRequiredColumns } from "./model";

const reportService = ReportService();
const PAGE_KEY = "erp_stock_min_alert";

export default function StockMinAlertAccess() {
  const [form] = Form.useForm();

  const [activeSearch, setActiveSearch] = useState([]);
  const [activeTab, setActiveTab] = useState("below_min");
  const [loading, setLoading] = useState(false);
  const [minAlertData, setMinAlertData] = useState({
    below_min: [],
    net_required: [],
  });

  const saveToCookie = useCallback((searchValues) => {
    saveAccessPageStateCookie(PAGE_KEY, { searchValues });
  }, []);

  const handleSearch = useCallback(
    (forcedValues = null) => {
      const run = (values) => {
        saveToCookie(values);
        setLoading(true);
        reportService
          .getStockMinAlert(values || {})
          .then((res) =>
            setMinAlertData(
              res?.data?.data || { below_min: [], net_required: [] },
            ),
          )
          .catch(() => message.error("โหลดรายงานสต๊อกขั้นต่ำไม่สำเร็จ"))
          .finally(() => setLoading(false));
      };

      if (forcedValues !== null) {
        run(forcedValues);
      } else {
        form.validateFields().then(run);
      }
    },
    [form, saveToCookie],
  );

  const handleClear = () => {
    clearAccessPageStateCookie(PAGE_KEY);
    form.resetFields();
    handleSearch({});
  };

  useEffect(() => {
    const restored = loadAccessPageStateCookie(PAGE_KEY);
    if (restored?.searchValues) {
      form.setFieldsValue(restored.searchValues);
    }
    handleSearch(restored?.searchValues ?? {});
  }, []);

  const belowMinCols = useMemo(() => minStockAlertColumns(), []);
  const netRequiredCols = useMemo(() => netRequiredColumns(), []);

  const searchPanel = (
    <Collapse
      size="small"
      onChange={setActiveSearch}
      bordered={false}
      activeKey={activeSearch}
      items={[
        {
          key: "1",
          label: (
            <>
              <SearchOutlined />
              <span> ค้นหา</span>
            </>
          ),
          showArrow: false,
          children: (
            <>
              <br />
              <Form form={form} layout="vertical" autoComplete="off">
                <Row gutter={[8, 8]}>
                  <Col xs={24} sm={8}>
                    <Form.Item label="รหัสสินค้า" name="stcode">
                      <Input placeholder="กรอกรหัสสินค้า" allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item label="ชื่อสินค้า" name="stname">
                      <Input placeholder="กรอกชื่อสินค้า" allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Flex
                      align="flex-end"
                      justify="flex-end"
                      style={{ height: "100%" }}
                      gap={8}
                    >
                      <Button
                        type="primary"
                        size="small"
                        className="bn-action"
                        icon={<SearchOutlined />}
                        onClick={() => handleSearch()}
                      >
                        ค้นหา
                      </Button>
                      <Button
                        type="primary"
                        size="small"
                        className="bn-action"
                        danger
                        icon={<ClearOutlined />}
                        onClick={handleClear}
                      >
                        ล้าง
                      </Button>
                    </Flex>
                  </Col>
                </Row>
              </Form>
            </>
          ),
        },
      ]}
    />
  );

  const tabItems = [
    {
      key: "below_min",
      label: (
        <Space size={6}>
          <span>ต่ำกว่าสต๊อกขั้นต่ำ</span>
          <Tag
            color={minAlertData.below_min.length > 0 ? "red" : "default"}
            style={{ margin: 0, borderRadius: 10 }}
          >
            {minAlertData.below_min.length}
          </Tag>
        </Space>
      ),
      children: (
        <Table
          size="small"
          rowKey="stcode"
          loading={loading}
          columns={belowMinCols}
          dataSource={minAlertData.below_min}
          scroll={{ x: "max-content" }}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          locale={{ emptyText: "ไม่มีสินค้าต่ำกว่าสต๊อกขั้นต่ำ" }}
        />
      ),
    },
    {
      key: "net_required",
      label: (
        <Space size={6}>
          <span>ต้องสั่งเพิ่ม (หลังหัก PO ค้างรับ)</span>
          <Tag
            color={minAlertData.net_required.length > 0 ? "orange" : "default"}
            style={{ margin: 0, borderRadius: 10 }}
          >
            {minAlertData.net_required.length}
          </Tag>
        </Space>
      ),
      children: (
        <Table
          size="small"
          rowKey="stcode"
          loading={loading}
          columns={netRequiredCols}
          dataSource={minAlertData.net_required}
          scroll={{ x: "max-content" }}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          locale={{ emptyText: "ไม่มีรายการที่ต้องสั่งเพิ่ม" }}
        />
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ display: "flex" }}>
      {searchPanel}
      <Card>
        <Flex
          justify="space-between"
          align="center"
          className="mb-3"
          wrap
          gap={8}
        >
          <Typography.Title level={3} className="m-0 !text-zinc-800">
            รายงานสต๊อกขั้นต่ำ
          </Typography.Title>
        </Flex>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          destroyInactiveTabPane={false}
        />
      </Card>
    </Space>
  );
}
